<script setup>
import { auth, fbConfig, firebaseConfig } from 'boot/firebase';
import { i18n } from 'boot/i18n';
import { CapacitorFirebaseMFA } from 'capacitor-firebase-mfa';
import AppleReauthenticate from 'components/Users/Modals/AppleReauthenticate.vue';
import { RecaptchaVerifier } from 'firebase/auth';
import { get } from 'lodash';
import { useDialogPluginComponent, useQuasar } from 'quasar';
import { SSO_PROVIDER } from 'src/constants/authorization';
import {
  HrAdaptiveFormDialog, HrConfirmBtn, HrDismissBtn, HrSubtleBtn, HrTextField,
  notifyNegative, openComponentDialog,
} from 'src/design-system';
import { auth as useAuthStore } from 'src/store/auth';
import { logEvent } from 'src/utils/LogEvent';
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';

defineOptions({ name: 'VerifyPhoneNumberMfa' });

const props = defineProps({
  resolver: {
    type: Object,
    required: true,
  },
  loginData: {
    type: Object,
    default: null,
  },
  provider: {
    type: String,
    default: null,
  },
  googleCredentials: {
    type: Object,
    default: null,
  },
  appleAuthData: {
    type: Object,
    default: null,
  },
});

defineEmits([...useDialogPluginComponent.emits]);

const $q = useQuasar();
const router = useRouter();
const authStore = useAuthStore();
const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();

const code = ref('');
const codeVerificationInProgress = ref(false);
const recaptchaVerifier = ref(null);
const verificationId = ref(null);
const sendSmsInProgress = ref(false);

const phoneHint = computed(() => {
  const phoneNumber = get(props.resolver, ['hints', 0, 'phoneNumber'], '');
  return phoneNumber ? `(${phoneNumber})` : '';
});

const isReady = computed(() => verificationId.value !== null);

async function processIosCapacitorGoogleMfa() {
  if (!props.googleCredentials) {
    return;
  }

  try {
    const { idToken, accessToken } = props.googleCredentials;
    const { value } = await CapacitorFirebaseMFA.loginWithGoogleAndSendMfaCode({ idToken, accessToken });
    verificationId.value = value;
  } catch (e) {
    notifyNegative(e.message);
    onDialogCancel();
  }
}

async function processIosCapacitorAppleMfa() {
  if (!props.appleAuthData) {
    return;
  }

  openComponentDialog(
    AppleReauthenticate,
    { appleAuthData: props.appleAuthData },
    (resolvedVerificationId) => {
      if (!resolvedVerificationId) {
        onDialogCancel();
        return;
      }
      verificationId.value = resolvedVerificationId;
    },
    () => {
      onDialogCancel();
    },
  );
}

async function processIosCapacitorPasswordMfa() {
  if (!props.loginData) {
    return;
  }

  try {
    const { email, password } = props.loginData;
    const { value } = await CapacitorFirebaseMFA.loginWithPasswordAndSendMfaCode({ email, password });
    verificationId.value = value;
  } catch (e) {
    notifyNegative(e.message);
    onDialogCancel();
  }
}

async function iosCapacitorHandleMfa() {
  try {
    await CapacitorFirebaseMFA.configure({
      appId: fbConfig.FIREBASE_APP_ID_IOS,
      apiKey: firebaseConfig.apiKey,
      projectId: firebaseConfig.projectId,
      authDomain: firebaseConfig.authDomain,
      databaseURL: firebaseConfig.databaseURL,
      storageBucket: firebaseConfig.storageBucket,
      messagingSenderId: firebaseConfig.messagingSenderId,
    });
  } catch (e) {
    logEvent(e);
    notifyNegative(e.message);
    onDialogCancel();
    return;
  }

  if (props.provider === SSO_PROVIDER.GOOGLE) {
    await processIosCapacitorGoogleMfa();
    return;
  }

  if (props.provider === SSO_PROVIDER.APPLE) {
    await processIosCapacitorAppleMfa();
    return;
  }

  await processIosCapacitorPasswordMfa();
}

async function handleMFA() {
  try {
    if (!recaptchaVerifier.value) {
      recaptchaVerifier.value = new RecaptchaVerifier(auth, 'mfa-captcha', {
        size: 'invisible',
      });
    }

    sendSmsInProgress.value = true;
    const result = await authStore.sendPhoneMfaCode({
      resolver: props.resolver,
      recaptchaVerifier: recaptchaVerifier.value,
    });
    verificationId.value = result.verificationId;
  } catch (err) {
    logEvent(err);
    notifyNegative(err.message);
    onDialogCancel();
  } finally {
    sendSmsInProgress.value = false;
  }
}

onMounted(async () => {
  if ($q.platform.is.capacitor && $q.platform.is.ios) {
    await iosCapacitorHandleMfa();
  } else {
    await handleMFA();
  }
});

async function onSubmitCode() {
  if (!code.value || verificationId.value === null) {
    return;
  }

  codeVerificationInProgress.value = true;
  try {
    const originalUserEmail = auth?.currentUser?.email;
    const userCredential = await authStore.verifyMFA({
      verificationId: verificationId.value,
      code: code.value,
      resolver: props.resolver,
    });

    if (!userCredential) {
      throw new Error(i18n.t('users.modals.verifyMFA.errorRetrievingUserCredentials'));
    }

    if (originalUserEmail && originalUserEmail !== userCredential.user.email) {
      notifyNegative(i18n.t('users.modals.reAuthenticate.emailIsNotMatch', { provider: 'Provider' }));
      onDialogCancel();
      await authStore.logout();
      router.push('/');
      return;
    }

    onDialogOK(userCredential);
  } catch (error) {
    if (error.code === 'auth/user-mismatch') {
      notifyNegative(i18n.t('users.modals.reAuthenticate.emailIsNotMatch', { provider: 'Provider' }));
      onDialogCancel();
      return;
    }

    if (error.code === 'auth/requires-recent-login') {
      notifyNegative(i18n.t('users.modals.verifyMFA.recentLoginRequiredError'));
      onDialogCancel();
      return;
    }

    notifyNegative(error.message);
  } finally {
    codeVerificationInProgress.value = false;
  }
}

async function onResendSms() {
  verificationId.value = null;

  if ($q.platform.is.capacitor && $q.platform.is.ios) {
    await iosCapacitorHandleMfa();
  } else {
    await handleMFA();
  }
}
</script>

<template>
  <hr-adaptive-form-dialog
    ref="dialogRef"
    :title="i18n.t('users.modals.verifyMFA.title')"
    @hide="onDialogHide"
    @close="onDialogCancel"
    @submit="onSubmitCode"
  >
    <p>{{ i18n.t('users.modals.verifyMFA.textSmsCaption', { phoneHint }) }}</p>
    <hr-text-field
      v-model="code"
      :loading="codeVerificationInProgress"
      :disable="codeVerificationInProgress"
      mask="######"
      autofocus
      :label="i18n.t('users.modals.verifyMFA.inputSmsCodeLabel')"
      :rules="[(val) => !!val || i18n.t('users.modals.verifyMFA.emptyCodeError')]"
      lazy-rules="ondemand"
      autocomplete="one-time-code"
    />
    <hr-subtle-btn
      :label="i18n.t('signup.enableMFA.resendCode')"
      :disable="sendSmsInProgress"
      @click="onResendSms"
    />
    <template #actions="{ isMobile }">
      <hr-dismiss-btn
        v-if="!isMobile"
        :label="i18n.t('common.buttonCancel')"
        @click="onDialogCancel"
      />
      <hr-confirm-btn
        type="submit"
        :label="i18n.t('users.modals.verifyMFA.buttonVerify')"
        :loading="codeVerificationInProgress"
        :disable="!isReady || !code"
      />
    </template>
  </hr-adaptive-form-dialog>
  <teleport to="body">
    <div id="mfa-captcha" />
  </teleport>
</template>
